import { useState, useCallback, useEffect } from 'react';
import { useGlobalAPI } from '../../../../hooks/useGlobalAPI';
import { makeRequest } from '../../../../services/apiClient';

/**
 * Custom hook for fetching available activities for Edge Time KPI.
 *
 * @param {string} activeFile - The currently active file
 * @returns {Object} Available activities and loading state
 */
export const useEdgeTimeAvailableActivities = (activeFile) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAvailableActivities = async () => {
      if (!activeFile) {
        setActivities([]);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log('🔄 Fetching available activities for edge time KPI:', activeFile);
        const result = await makeRequest(`/kpi/edge-time/activities?file_type=${activeFile}`, {}, 'Edge Time Available Activities');

        if (result && result.activities) {
          setActivities(result.activities);
          console.log('✅ Available activities loaded:', result.activities.length, 'activities');
        } else {
          setActivities([]);
          console.log('⚠️ No available activities found');
        }
      } catch (err) {
        console.error('❌ Error fetching available activities:', err);
        setError(err.message || 'Failed to fetch available activities');
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableActivities();
  }, [activeFile]);

  return {
    activities,
    loading,
    error,
    hasData: activities.length > 0
  };
};

/**
 * Custom hook for Edge Time KPI calculation.
 *
 * Calculates time statistics between two activities (edges) in the performance graph.
 *
 * @param {string} activeFile - The currently active file
 * @returns {object} KPI data, loading state, error state, and fetch function
 */
export const useEdgeDuration = (activeFile) => {
  // ✅ Use unified API hook - automatically applies global filters
  const api = useGlobalAPI(activeFile);

  // State for KPI data and UI
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchKPIData = useCallback(async (fromActivity, toActivity) => {
    if (!activeFile) {
      setError("No active file selected");
      return;
    }

    // Validate inputs
    if (!fromActivity || !toActivity) {
      setError("Please select both From and To activities");
      return;
    }

    if (fromActivity === toActivity) {
      setError("From and To activities must be different");
      return;
    }

    setLoading(true);
    setError(null);
    setHasFetched(true);

    try {
      console.log("🔄 Fetching edge time KPI data...", {
        activeFile,
        fromActivity,
        toActivity,
      });

      const requestPayload = {
        from_activity: fromActivity,
        to_activity: toActivity,
      };
      const response = await api.post('/kpi/edge-time', requestPayload, {}, 'Edge Time KPI');

      console.log("📥 Edge Time KPI response received:", response);
      setData(response);
    } catch (err) {
      console.error("❌ Error during Edge Time KPI fetch:", err);
      setError(err.message || "An unknown error occurred.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [activeFile, api]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setHasFetched(false);
    setLoading(false);
  }, []);

  return {
    data,
    loading,
    error,
    hasFetched,
    fetchKPIData,
    reset
  };
};
